/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Trans, useTranslation } from "react-i18next";
import { ERROR_USER_ALREADY_EXISTS } from "../../Constants";
import { isFeatureOn } from "../../portalSettings";

/**
 * הפרטים אינם תואמים ללקוח שכבר קיים אצלנו.
 *
 * ההודעה עצמה מפנה לתמיכה, וזה נכון: אנחנו באמת לא יכולים להכריע מהטופס הזה שמי
 * שממלא אותו הוא בעל החשבון. אבל **הוא כן יכול להוכיח את זה בעצמו** - בהתחברות
 * מהירה, שכל כולה הוכחת אחזקה בערוץ שרשום על החשבון. מי שהוא באמת הלקוח נכנס תוך
 * חצי דקה במקום להתקשר, ומי שאינו - אינו מקבל שום דבר שלא היה לו קודם.
 */
const ERROR_CUSTOMER_EXISTS_NO_DATA_MATCH = "customer_exists_no_data_match";

/**
 * שגיאות הטופס, ובתוכן מקרה אחד שדורש יותר מטקסט: משתמש קיים.
 *
 * `user_already_exists` הוא סוף הדרך במסך שהוא הופיע בו - אין שדה לתקן ולנסות שוב, כי
 * המשתמש כבר קיים אצלנו. ההודעה אומרת להתחבר אליו, והשורה שמתחתיה עושה את זה במקום
 * להשאיר את הלקוח לחפש את מסך ההתחברות בעצמו.
 *
 * ברכיב אחד ולא בשני מסכים: ההרשמה ויצירת המשתמש מקבלות את אותה שגיאה משני השירותים
 * שמאחוריהן, ושני עותקים של אותו טיפול הם שתי הזדמנויות שאחד מהם יישאר מאחור.
 */
const FormErrors = ({ errors }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const reseller = useSelector((state) => state.reseller.value);
  const quickLoginAvailable = isFeatureOn(reseller, "allowQuickLogin");

  const messages = Object.entries(errors || {});
  const userAlreadyExists = messages.some(([, msg]) => msg === ERROR_USER_ALREADY_EXISTS);
  const detailsDoNotMatch = messages.some(
    ([, msg]) => msg === ERROR_CUSTOMER_EXISTS_NO_DATA_MATCH
  );

  /**
   * שני המצבים מובילים לאותו מקום, ובכל זאת נאמרים אחרת.
   *
   * "המשתמש קיים" הוא סוף מוצלח שנקטע - צריך רק להיכנס. "הפרטים אינם תואמים" הוא
   * סירוב, וההצעה שאחריו היא הצעה: להוכיח אחזקה בערוץ במקום לפנות אלינו. כיתוב אחד
   * לשניהם היה קורא לסירוב הצלחה.
   */
  const offer = userAlreadyExists
    ? quickLoginAvailable
      ? "Sign in with a one-time code"
      : "Go to the login screen"
    : detailsDoNotMatch && quickLoginAvailable
      ? "If this is your account, you can sign in with a one-time code"
      : null;

  return (
    <>
      {messages.map(([key, msg]) => (
        <h2 key={key} className="mt20 error">
          <Trans i18nKey={msg} />
        </h2>
      ))}
      {offer ? (
        <h2 className="mt20 teal pointer" onClick={() => navigate("/login")}>
          {t(offer)}
        </h2>
      ) : (
        ""
      )}
    </>
  );
};

export default FormErrors;
